import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Search, Pencil, Eye, FileText, CheckCircle2, Ban, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const dueDefault = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

const emptyLine = () => ({ description: "", quantity: 1, unit_price: 0, discount: 0, tax_rate: 15 });
const emptyForm = () => ({
  invoice_number: "",
  issue_date: today(),
  due_date: dueDefault(),
  customer_name: "",
  customer_vat: "",
  customer_address: "",
  customer_email: "",
  project_name: "",
  currency: "SAR",
  notes: "",
  payment_terms: "Net 30 days",
  items: [emptyLine()],
});

export default function InvoicesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/invoices");
      setItems(data?.items || []);
    } catch { toast.error("Failed to load invoices"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (inv) => {
    setEditing(inv.id);
    setForm({
      ...emptyForm(),
      ...inv,
      items: inv.items && inv.items.length ? inv.items : [emptyLine()],
    });
    setOpen(true);
  };

  const totals = useMemo(() => {
    let sub = 0, disc = 0, tax = 0;
    form.items.forEach((it) => {
      const q = parseFloat(it.quantity) || 0;
      const p = parseFloat(it.unit_price) || 0;
      const d = parseFloat(it.discount) || 0;
      const r = parseFloat(it.tax_rate) || 0;
      const line = q * p;
      const taxable = Math.max(line - d, 0);
      sub += line;
      disc += d;
      tax += taxable * (r / 100);
    });
    return { subtotal: sub, discount: disc, tax, total: sub - disc + tax };
  }, [form.items]);

  const updateLine = (idx, key, value) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: value } : it) }));
  };
  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, emptyLine()] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, i) => i !== idx) : f.items }));

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
    if (!form.issue_date) { toast.error("Issue date is required"); return; }
    if (!form.items.some((it) => it.description.trim() && parseFloat(it.unit_price) > 0)) {
      toast.error("Add at least one line item with description and price");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 0,
          unit_price: parseFloat(it.unit_price) || 0,
          discount: parseFloat(it.discount) || 0,
          tax_rate: parseFloat(it.tax_rate) || 0,
        })),
      };
      if (editing) {
        await api.put(`/admin/invoices/${editing}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/admin/invoices", payload);
        toast.success("Invoice created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/invoices/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Could not update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/admin/invoices/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((inv) => {
      const matchSearch = !q
        || inv.invoice_number.toLowerCase().includes(q)
        || inv.customer_name.toLowerCase().includes(q)
        || (inv.project_name || "").toLowerCase().includes(q);
      const matchStatus = statusFilter === "all" || inv.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [items, search, statusFilter]);

  const sumIssued = items.filter((i) => i.status === "issued").reduce((s, i) => s + i.total, 0);
  const sumPaid = items.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <header className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <span className="overline">/ Billing</span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-black">
            Invoices <span className="text-primary">·</span> ZATCA QR
          </h1>
          <p className="text-muted-foreground mt-2">
            Issue compliant invoices with embedded ZATCA QR code for Saudi e-invoicing.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-sm h-11 bg-secondary hover:bg-primary text-secondary-foreground hover:text-primary-foreground" data-testid="inv-add-btn">
          <Plus className="h-4 w-4 mr-2" /> New Invoice
        </Button>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Invoices" value={items.length} />
        <StatCard label="Issued" value={items.filter((i) => i.status === "issued").length} />
        <StatCard label="Outstanding (SAR)" value={sumIssued.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tone="primary" />
        <StatCard label="Paid (SAR)" value={sumPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search number, customer, project…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm h-11 w-80 pl-9" data-testid="inv-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-sm h-11 w-44" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="issued">Issued</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-sm overflow-hidden">
        <Table data-testid="inv-table">
          <TableHeader>
            <TableRow className="bg-muted/60">
              <TableHead>Number</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Project</TableHead>
              <TableHead className="text-end">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-end">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                No invoices yet. Click <strong>New Invoice</strong> to issue one.
              </TableCell></TableRow>
            )}
            {filtered.map((inv) => (
              <TableRow key={inv.id} data-testid={`inv-row-${inv.id}`}>
                <TableCell className="font-mono font-bold">{inv.invoice_number}</TableCell>
                <TableCell className="text-sm">{inv.issue_date}</TableCell>
                <TableCell>
                  <div className="font-semibold">{inv.customer_name}</div>
                  {inv.customer_vat && <div className="text-xs text-muted-foreground">VAT {inv.customer_vat}</div>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{inv.project_name || "—"}</TableCell>
                <TableCell className="text-end font-bold tabular-nums">
                  {inv.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-xs text-muted-foreground">{inv.currency}</span>
                </TableCell>
                <TableCell><StatusPill status={inv.status} /></TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Link to={`/admin/invoices/${inv.id}`}>
                      <Button variant="ghost" size="icon" data-testid={`inv-view-${inv.id}`} title="View / Print">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(inv)} data-testid={`inv-edit-${inv.id}`} title="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {inv.status !== "paid" && (
                      <Button variant="ghost" size="icon" onClick={() => setStatus(inv.id, "paid")} title="Mark paid">
                        <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                      </Button>
                    )}
                    {inv.status !== "cancelled" && (
                      <Button variant="ghost" size="icon" onClick={() => setStatus(inv.id, "cancelled")} title="Cancel">
                        <Ban className="h-4 w-4 text-amber-700" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => remove(inv.id)} title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="inv-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {editing ? "Edit invoice" : "New invoice"}
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto pr-1 flex-1 space-y-6">
            {/* Header info */}
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Invoice number" hint="(auto if blank)">
                <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="rounded-sm font-mono" data-testid="inv-input-number" placeholder="MNH-2026-00001" />
              </Field>
              <Field label="Issue date *">
                <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="rounded-sm" data-testid="inv-input-issue" />
              </Field>
              <Field label="Due date">
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm" data-testid="inv-input-due" />
              </Field>
            </div>

            {/* Customer */}
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-primary font-bold mb-3">Bill To</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Customer name *">
                  <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="rounded-sm" data-testid="inv-input-customer_name" />
                </Field>
                <Field label="Customer VAT">
                  <Input value={form.customer_vat} onChange={(e) => setForm({ ...form, customer_vat: e.target.value })} className="rounded-sm font-mono" data-testid="inv-input-customer_vat" />
                </Field>
                <Field label="Customer email">
                  <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="rounded-sm" data-testid="inv-input-customer_email" />
                </Field>
                <Field label="Project / Reference">
                  <Input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} className="rounded-sm" data-testid="inv-input-project" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Customer address">
                    <Textarea rows={2} value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="rounded-sm" data-testid="inv-input-customer_address" />
                  </Field>
                </div>
              </div>
            </div>

            {/* Line items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.18em] text-primary font-bold">Line items</div>
                <Button variant="outline" size="sm" onClick={addLine} className="rounded-sm" data-testid="inv-add-line">
                  <Plus className="h-3 w-3 mr-1" /> Add line
                </Button>
              </div>
              <div className="border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-xs uppercase tracking-[0.14em]">
                    <tr>
                      <th className="text-start p-2 w-1/2">Description</th>
                      <th className="text-end p-2 w-20">Qty</th>
                      <th className="text-end p-2 w-28">Unit Price</th>
                      <th className="text-end p-2 w-24">Discount</th>
                      <th className="text-end p-2 w-20">VAT %</th>
                      <th className="text-end p-2 w-28">Line Total</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, idx) => {
                      const line = (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0);
                      const taxable = Math.max(line - (parseFloat(it.discount) || 0), 0);
                      const lineTotal = taxable * (1 + (parseFloat(it.tax_rate) || 0) / 100);
                      return (
                        <tr key={idx} className="border-t border-border" data-testid={`inv-line-${idx}`}>
                          <td className="p-1.5"><Input value={it.description} onChange={(e) => updateLine(idx, "description", e.target.value)} className="rounded-sm h-9 text-sm" placeholder="Service / item description" data-testid={`inv-line-desc-${idx}`} /></td>
                          <td className="p-1.5"><Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} className="rounded-sm h-9 text-end text-sm" /></td>
                          <td className="p-1.5"><Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateLine(idx, "unit_price", e.target.value)} className="rounded-sm h-9 text-end text-sm" data-testid={`inv-line-price-${idx}`} /></td>
                          <td className="p-1.5"><Input type="number" step="0.01" value={it.discount} onChange={(e) => updateLine(idx, "discount", e.target.value)} className="rounded-sm h-9 text-end text-sm" /></td>
                          <td className="p-1.5"><Input type="number" step="0.01" value={it.tax_rate} onChange={(e) => updateLine(idx, "tax_rate", e.target.value)} className="rounded-sm h-9 text-end text-sm" /></td>
                          <td className="p-1.5 text-end font-mono tabular-nums">{lineTotal.toFixed(2)}</td>
                          <td className="p-1.5"><Button variant="ghost" size="icon" onClick={() => removeLine(idx)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Field label="Payment terms">
                  <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="rounded-sm" />
                </Field>
                <div className="mt-3">
                  <Field label="Notes">
                    <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" />
                  </Field>
                </div>
              </div>
              <div className="bg-muted/40 p-5 rounded-sm space-y-2">
                <TotalRow label="Subtotal" value={totals.subtotal} />
                <TotalRow label="Discount" value={-totals.discount} muted />
                <TotalRow label="VAT" value={totals.tax} muted />
                <div className="h-px bg-border my-2" />
                <TotalRow label="Total (SAR)" value={totals.total} big />
              </div>
            </div>
          </div>

          <DialogFooter className="border-t border-border pt-4 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="inv-save-btn">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : (editing ? "Save changes" : "Issue invoice")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] mb-1 block">
        {label} {hint && <span className="normal-case tracking-normal text-muted-foreground font-normal">{hint}</span>}
      </Label>
      {children}
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`p-5 rounded-sm border ${tone === "primary" ? "bg-primary/[0.06] border-primary/30" : "bg-card border-border"}`}>
      <div className={`text-3xl font-black tabular-nums ${tone === "primary" ? "text-primary" : ""}`}>{value}</div>
      <div className="mt-2 text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{label}</div>
    </div>
  );
}

function TotalRow({ label, value, big, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${big ? "text-base font-bold" : "text-sm"} ${muted ? "text-muted-foreground" : ""}`}>{label}</span>
      <span className={`${big ? "text-xl font-black text-primary" : "text-sm font-semibold"} tabular-nums`}>{value.toFixed(2)}</span>
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "draft") return <Badge variant="outline" className="rounded-sm">Draft</Badge>;
  if (status === "issued") return <Badge className="rounded-sm bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200">Issued</Badge>;
  if (status === "paid") return <Badge className="rounded-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200">Paid</Badge>;
  if (status === "cancelled") return <Badge className="rounded-sm bg-amber-100 text-amber-800 hover:bg-amber-100 border-amber-200">Cancelled</Badge>;
  return <Badge variant="outline" className="rounded-sm">{status}</Badge>;
}
